import { useEffect, useRef } from 'react'
import { getNavSnapshot, setNavSnapshot } from './scopedNavStateRegistry'

export interface NavStateOwner {
  focusedNavIndex: number | null
  selectedIds: Set<string>
  setFocusedNavIndex: (idx: number | null) => void
  setSelectedIds: (ids: Set<string>) => void
  clearSelection: () => void
  /**
   * Optional: called after a successful restore to hand keyboard focus to
   * the list wrapper. Without this the Arrow keys won't navigate because
   * the browser focus is still on the tab button the user just clicked.
   */
  focusList?: () => void
}

const isEmpty = (focusedNavIndex: number | null, selectedIds: Set<string>) =>
  focusedNavIndex === null && selectedIds.size === 0

/**
 * Persists the caller's navigation selection (focused index + selected ids)
 * per `scopeKey` (usually the route pathname). State survives component
 * unmount/remount for the lifetime of the session, so switching between tabs
 * or life spaces and returning to a previously-visited scope restores the
 * selection as it was left.
 *
 * Empty states are **intentionally not persisted**. Various global handlers
 * (e.g. click-outside in useProjectNavigation) fire `clearSelection()` right
 * before the page unmounts on a tab switch. If we saved those empty states,
 * every tab switch would wipe the registry. Skipping empty writes keeps the
 * last non-empty snapshot intact, which is what the user actually wants.
 */
export function useScopedNavState(scopeKey: string, nav: NavStateOwner): void {
  const navRef = useRef(nav)
  navRef.current = nav

  const prevScopeRef = useRef<string | null>(null)

  // On mount + scope change: snapshot the outgoing scope (if non-empty),
  // then restore or clear for the incoming scope.
  useEffect(() => {
    const prev = prevScopeRef.current

    if (prev !== null && prev !== scopeKey) {
      const outgoing = {
        focusedNavIndex: navRef.current.focusedNavIndex,
        selectedIds: navRef.current.selectedIds,
      }
      if (!isEmpty(outgoing.focusedNavIndex, outgoing.selectedIds)) {
        setNavSnapshot(prev, outgoing)
      }
    }

    const saved = getNavSnapshot(scopeKey)
    if (saved) {
      navRef.current.setFocusedNavIndex(saved.focusedNavIndex)
      navRef.current.setSelectedIds(saved.selectedIds)
      navRef.current.focusList?.()
    } else if (prev !== null && prev !== scopeKey) {
      navRef.current.clearSelection()
    }

    prevScopeRef.current = scopeKey
  }, [scopeKey])

  // Continuous save: every non-empty nav change updates the registry so the
  // current scope always has the latest user-visible selection on record.
  useEffect(() => {
    const scope = prevScopeRef.current
    if (scope === null) return
    if (isEmpty(nav.focusedNavIndex, nav.selectedIds)) return
    setNavSnapshot(scope, {
      focusedNavIndex: nav.focusedNavIndex,
      selectedIds: nav.selectedIds,
    })
  }, [nav.focusedNavIndex, nav.selectedIds])

  // Unmount safety net — the continuous save above should already have the
  // latest non-empty snapshot, but if unmount happens in the same commit as
  // a final non-empty update we still want to catch it here.
  useEffect(() => {
    return () => {
      const scope = prevScopeRef.current
      if (scope === null) return
      if (isEmpty(navRef.current.focusedNavIndex, navRef.current.selectedIds)) return
      setNavSnapshot(scope, {
        focusedNavIndex: navRef.current.focusedNavIndex,
        selectedIds: navRef.current.selectedIds,
      })
    }
  }, [])
}
